function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
var _onKeyUp = _classPrivateFieldLooseKey("onKeyUp");
var _getDetailsElement = _classPrivateFieldLooseKey("getDetailsElement");
var _close = _classPrivateFieldLooseKey("close");
var _setupAnimatedElementListeners = _classPrivateFieldLooseKey("setupAnimatedElementListeners");
class HeaderDrawer extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Attach animationend event listeners to all animated elements to remove will-change after animation
     * to remove the stacking context and allow submenus to be positioned correctly
     */
    Object.defineProperty(this, _setupAnimatedElementListeners, {
      value: _setupAnimatedElementListeners2
    });
    /**
     * Close the closest menu or submenu that is open
     *
     * @param {HTMLDetailsElement} details
     */
    Object.defineProperty(this, _close, {
      value: _close2
    });
    /**
     * Get the closest details element to the event target
     * @param {Event | undefined} event
     * @returns {HTMLDetailsElement}
     */
    Object.defineProperty(this, _getDetailsElement, {
      value: _getDetailsElement2
    });
    this.requiredRefs = ['details', 'menuDrawer'];
    /**
     * Close the main menu drawer when the Escape key is pressed
     * @param {KeyboardEvent} event
     */
    Object.defineProperty(this, _onKeyUp, {
      writable: true,
      value: event => {
        if (event.key !== 'Escape') return;
        _classPrivateFieldLooseBase(this, _close)[_close](_classPrivateFieldLooseBase(this, _getDetailsElement)[_getDetailsElement](event));
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', _classPrivateFieldLooseBase(this, _onKeyUp)[_onKeyUp]);
    _classPrivateFieldLooseBase(this, _setupAnimatedElementListeners)[_setupAnimatedElementListeners]();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', _classPrivateFieldLooseBase(this, _onKeyUp)[_onKeyUp]);
  }
  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }
  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = _classPrivateFieldLooseBase(this, _getDetailsElement)[_getDetailsElement](event);
    const summary = details.querySelector('summary');
    if (!summary) return;
    summary.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), {
        subtree: false
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    _classPrivateFieldLooseBase(this, _close)[_close](_classPrivateFieldLooseBase(this, _getDetailsElement)[_getDetailsElement](event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    _classPrivateFieldLooseBase(this, _close)[_close](this.refs.details);
  }
  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');
    content.forEach(element => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach(element => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}
function _getDetailsElement2(event) {
  if (!(event?.target instanceof Element)) return this.refs.details;
  return event.target.closest('details') ?? this.refs.details;
}
function _close2(details) {
  const summary = details.querySelector('summary');
  if (!summary) return;
  summary.setAttribute('aria-expanded', 'false');
  details.classList.remove('menu-open');
  this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

  // Wait for the .menu-drawer element's transition, not the entire details subtree
  // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
  const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
  onAnimationEnd(drawer || details, () => {
    reset(details);
    if (details === this.refs.details) {
      removeTrapFocus();
      const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
      openDetails.forEach(reset);
    } else {
      trapFocus(this.refs.details);
    }
  }, {
    subtree: false
  });
}
function _setupAnimatedElementListeners2() {
  const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
  allAnimated.forEach(element => {
    element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
  });
}
if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
